<template>
    <CRow>
        <CCol col="12" xl="12">
            <CCard>
                <CCardHeader>
                    <strong>Сегмент: {{ segment.name }}</strong>
                </CCardHeader>
                <CCardBody>
                    <div class="table__preloader" v-if="loading">
                        <CSpinner color="primary"/>
                    </div>
                    <div v-show="!loading">
                        <CRow class="mb-4">
                            <CCol cols="12" md="6">
                                <h5>Информация о сегменте</h5>
                                <table class="table table-striped">
                                    <tbody>
                                        <tr>
                                            <td><strong>ID:</strong></td>
                                            <td>{{ segment.id }}</td>
                                        </tr>
                                        <tr>
                                            <td><strong>Название:</strong></td>
                                            <td>{{ segment.name }}</td>
                                        </tr>
                                        <tr>
                                            <td><strong>Количество клиентов:</strong></td>
                                            <td>{{ segment.customer_ids ? segment.customer_ids.length : 0 }}</td>
                                        </tr>
                                        <tr>
                                            <td><strong>Последнее обновление:</strong></td>
                                            <td>{{ segment.last_updated_at ? formatDate(segment.last_updated_at) : 'Никогда' }}</td>
                                        </tr>
                                        <tr>
                                            <td><strong>Частота обновления:</strong></td>
                                            <td>{{ getUpdateFrequencyLabel(segment.update_frequency_hours) }}</td>
                                        </tr>
                                        <tr v-if="segment.last_updated_at && segment.update_frequency_hours">
                                            <td><strong>Следующее обновление:</strong></td>
                                            <td>{{ getNextUpdateTime(segment.last_updated_at, segment.update_frequency_hours) }}</td>
                                        </tr>
                                        <tr>
                                            <td><strong>Активен:</strong></td>
                                            <td>
                                                <CBadge :color="getBadge(segment.enabled)">&nbsp;{{
                                                    segment.enabled ? "Да" : "Нет"
                                                }}&nbsp;</CBadge>
                                            </td>
                                        </tr>
                                        <tr v-if="segment.is_system">
                                            <td><strong>Системный:</strong></td>
                                            <td>
                                                <CBadge color="info">&nbsp;Да&nbsp;</CBadge>
                                                <small class="ml-2 text-muted">Защищен от удаления</small>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td><strong>Дата создания:</strong></td>
                                            <td>{{ formatDate(segment.created_at) }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                                <div class="mt-2" style="display: flex; justify-content: space-between; align-items: center;">
                                    <CButton
                                        color="warning"
                                        class="mr-2"
                                        @click="editSegment">
                                        Редактировать
                                    </CButton>
                                    <CButton
                                        v-if="!segment.is_system"
                                        color="danger"
                                        class="ml-2"
                                        @click="deleteSegment">
                                        Удалить
                                    </CButton>
                                    <small v-else class="text-muted ml-2">
                                        Системный сегмент не может быть удален
                                    </small>
                                </div>
                            </CCol>
                            <CCol cols="12" md="6">
                                <h5>Фильтры сегмента</h5>
                                <pre class="bg-light p-3 rounded text-black">{{ JSON.stringify(segment.filters, null, 2) }}</pre>
                            </CCol>
                        </CRow>

                        <CRow class="mb-4" v-if="segment.sql_query">
                            <CCol cols="12">
                                <h5>SQL запрос</h5>
                                <pre class="bg-dark p-3 rounded text-light sql-query">{{ segment.sql_query }}</pre>
                                <small class="form-text text-muted">
                                    SQL запрос, сформированный на основе фильтров сегмента для получения списка клиентов.
                                </small>
                            </CCol>
                        </CRow>

                        <div class="mt-2 mb-4" style="display: flex; justify-content: space-between; align-items: center;">
                            <CButton
                                color="info"
                                class="mr-2"
                                @click="refreshSegment">
                                Обновить сегмент
                            </CButton>
                            <CButton
                                color="primary"
                                class="ml-2"
                                @click="push.modalVisible = true"
                                :disabled="!segment.customer_ids || segment.customer_ids.length === 0">
                                Отправить push-уведомление
                            </CButton>
                        </div>

                        <h5>Клиенты в сегменте</h5>
                        <div v-if="customers.length > 0">
                            <CDataTable
                                hover
                                :items="customers"
                                :fields="customerFields">
                                <template #id="data">
                                    <td>
                                        <CLink :to="`/customers/${data.item.id}`">{{
                                            data.item.id
                                        }}</CLink>
                                    </td>
                                </template>
                                <template #phone="data">
                                    <td>
                                        <CLink :to="`/customers/${data.item.id}`">{{
                                            data.item.phone
                                        }}</CLink>
                                    </td>
                                </template>
                                <template #firstname="data">
                                    <td>
                                        {{ data.item.firstname }}
                                    </td>
                                </template>
                                <template #lastname="data">
                                    <td>
                                        {{ data.item.lastname }}
                                    </td>
                                </template>
                                <template #created_at="data">
                                    <td>
                                        {{ formatDate(data.item.created_at) }}
                                    </td>
                                </template>
                            </CDataTable>
                            <CPagination
                                :active-page.sync="customerPage"
                                class="mt-4"
                                :pages="customerTotalPages"
                                align="center"
                            />
                        </div>
                        <div v-else class="text-center text-muted">
                            <p>В этом сегменте пока нет клиентов</p>
                        </div>
                    </div>
                </CCardBody>
            </CCard>
        </CCol>
        <CModal
            title="Push-уведомление"
            :show.sync="push.modalVisible"
            :footer="false"
        >
            <template>
                <div slot="body-wrapper" class="modal-body">
                    <p class="mb-2">
                        Push-уведомление будет отправлено <b>{{ segment.customer_ids ? segment.customer_ids.length : 0 }}</b> клиенту(ам) из сегмента "{{ segment.name }}"
                    </p>

                    <CRow class="mt-1">
                        <CCol
                            cols="12"
                            md="4"
                            class="mt-1 d-flex align-items-center">
                            Дата и время отправки
                        </CCol>
                        <CCol
                            cols="12"
                            md="8"
                            class="mt-1 d-flex align-items-center">
                            <DatePicker
                                v-model="push.started_date"
                                :language="datepicker.lang"
                                :format="datepicker.format"
                                class="w-full"
                                input-class="form-control custom-datepicker"
                                placeholder="Дата и время отправки" 
                                hide-details />
                            <CInput
                                v-model="push.started_time"
                                type="time"
                                class="m-0"
                            />
                        </CCol>
                    </CRow>
                    <CRow class="mt-1">
                        <CCol
                            cols="12"
                            md="4"
                            class="mt-1 d-flex align-items-center">
                            Заголовок
                        </CCol>
                        <CCol
                            cols="12"
                            md="8"
                            class="mt-1 d-flex align-items-center">
                            <CInput 
                                v-model="push.title" 
                                class="w-full m-0"
                                placeholder="Заголовок" />
                        </CCol>
                    </CRow>
                    <CRow class="mt-1">
                        <CCol
                            cols="12"
                            md="4"
                            class="mt-1 d-flex align-items-center">
                            Текст сообщения
                        </CCol>
                        <CCol
                            cols="12"
                            md="8"
                            class="mt-1 d-flex align-items-center">
                            <CTextarea 
                                v-model="push.text" 
                                class="w-full m-0"
                                placeholder="Текст" />
                        </CCol>
                    </CRow>
                    <CRow class="mt-1">
                        <CCol
                            cols="12"
                            md="4"
                            class="mt-1 d-flex align-items-center">
                            Страница, которая открывается при клике на push
                        </CCol>
                        <CCol
                            cols="12"
                            md="8"
                            class="mt-1 d-flex align-items-center">
                            <CInput 
                                v-model="push.link" 
                                class="w-full m-0"
                                placeholder="Ссылка" />
                        </CCol>
                    </CRow>
                </div>
                <div slot="footer">
                    <CButton
                        color="light"
                        class="ml-3"
                        @click="push.modalVisible = false"
                    >Отмена</CButton
                    >
                    <CButton
                        color="primary"
                        class="ml-3"
                        @click="sendPush"
                    >Отправить</CButton
                    >
                </div>
            </template>
        </CModal>
    </CRow>
</template>

<script>
    import Vue from 'vue';
    import DatePicker from 'vuejs-datepicker';
    import { ru } from 'vuejs-datepicker/dist/locale';
    const { formatDate } = require('../../utils/views');

    export default {
        name: 'Segment',

        components: {
            DatePicker,
        },

        data() {
            return {
                loading: true,
                segment: {},
                customers: [],
                customerPage: 1,
                customerTotalPages: 1,
                customerFields: [
                    { key: 'id', label: 'ID', _classes: 'font-weight-bold' },
                    { key: 'phone', label: 'Телефон' },
                    { key: 'firstname', label: 'Имя' },
                    { key: 'lastname', label: 'Фамилия' },
                    { key: 'created_at', label: 'Дата регистрации' }
                ],
                push: {
                    started_at: null,
                    started_date: null,
                    started_time: null,
                    modalVisible: false,
                    title: '',
                    text: '',
                    link: '',
                },
                datepicker: {
                    lang: ru,
                    format: 'dd.MM.yyyy',
                    dateFrom: null,
                    dateTo: null,
                },
            };
        },

        async mounted() {
            await this.fetch();
            await this.fetchCustomers();
            this.loading = false;
        },

        watch: {
            customerPage: {
                handler() {
                    this.fetchCustomers();
                }
            }
        },

        methods: {
            formatDate,
            
            getBadge(status) {
                return status ? 'success' : 'secondary';
            },

            getUpdateFrequencyLabel(hours) {
                if (!hours) {
                    return 'Стандартная (раз в неделю)';
                }
                const labels = {
                    1: 'Каждый час',
                    2: 'Каждые 2 часа',
                    3: 'Каждые 3 часа',
                    6: 'Каждые 6 часов',
                    12: 'Каждые 12 часов',
                    24: 'Каждые 24 часа (раз в день)',
                    48: 'Каждые 48 часов (раз в 2 дня)',
                    72: 'Каждые 72 часа (раз в 3 дня)',
                    168: 'Каждые 168 часов (раз в неделю)'
                };
                return labels[hours] || `Каждые ${hours} часов`;
            },

            getNextUpdateTime(lastUpdatedAt, updateFrequencyHours) {
                if (!lastUpdatedAt || !updateFrequencyHours) {
                    return 'Не определено';
                }
                const lastUpdate = new Date(lastUpdatedAt);
                const nextUpdate = new Date(lastUpdate.getTime() + updateFrequencyHours * 60 * 60 * 1000);
                return this.formatDate(nextUpdate);
            },

            async fetch() {
                try {
                    const segmentId = this.$route.params.id;
                    const response = await Vue.axios.get(`/segments/${segmentId}`);
                    this.segment = response.data;
                } catch (err) {
                    this.$root.$emit('showError', 'Ошибка загрузки сегмента.');
                    console.error(err);
                }
            },

            async fetchCustomers() {
                try {
                    const segmentId = this.$route.params.id;
                    const response = await Vue.axios.get(`/segments/${segmentId}/customers`, {
                        params: {
                            page: this.customerPage,
                            limit: 20
                        }
                    });
                    this.customers = response.data.data;
                    this.customerTotalPages = response.data.pagination.totalPages;
                } catch (err) {
                    this.$root.$emit('showError', 'Ошибка загрузки клиентов сегмента.');
                    console.error(err);
                }
            },

            async refreshSegment() {
                try {
                    const segmentId = this.$route.params.id;
                    await Vue.axios.post(`/segments/${segmentId}/refresh`);
                    this.$root.$emit('showSuccess', 'Сегмент обновлен.');
                    await this.fetch();
                    await this.fetchCustomers();
                } catch (err) {
                    this.$root.$emit('showError', 'Ошибка обновления сегмента.');
                    console.error(err);
                }
            },

            editSegment() {
                this.$router.push(`/segments/${this.segment.id}/edit`);
            },

            async deleteSegment() {
                if (this.segment.is_system) {
                    this.$root.$emit('showError', 'Системный сегмент не может быть удален.');
                    return;
                }
                
                if (confirm('Вы уверены, что хотите удалить этот сегмент?')) {
                    try {
                        const segmentId = this.$route.params.id;
                        await Vue.axios.delete(`/segments/${segmentId}`);
                        this.$root.$emit('showSuccess', 'Сегмент удален.');
                        this.$router.push('/segments');
                    } catch (err) {
                        if (err.response && err.response.status === 403) {
                            this.$root.$emit('showError', 'Системный сегмент не может быть удален.');
                        } else {
                            this.$root.$emit('showError', 'Ошибка удаления сегмента.');
                        }
                        console.error(err);
                    }
                }
            },

            sendPush() {
                const this_ = this;
                if(!this.push.title) {
                    this.$root.$emit('showError', 'Введите заголовок push-уведомления.');
                    return;
                }
                if(!this.push.text) {
                    this.$root.$emit('showError', 'Введите текст push-уведомления.');
                    return;
                }
                
                const segmentId = this.$route.params.id;
                Vue.axios
                    .post(`/segments/${segmentId}/send-push`, { 
                        ...this.push
                    })
                    .then(resp => {
                        this_.push.modalVisible = false;
                        const data = resp.data;
                        if (data && data.success === false) {
                            this.$root.$emit('showError', data.message);
                        } else {
                            const msg = data && data.message
                                ? data.message
                                : `Push-уведомление поставлено в очередь для клиентов сегмента "${this.segment.name}".`;
                            this.$root.$emit('showSuccess', msg);
                        }
                        this.push.title = '';
                        this.push.text = '';
                        this.push.link = '';
                        this.push.started_date = null;
                        this.push.started_time = null;
                    })
                    .catch(err => {
                        this.$root.$emit('showError', 'Ошибка отправки push-уведомления. ' + err);
                    });
            },
        }
    };
</script>

<style scoped>
.sql-query {
    font-family: 'Courier New', monospace;
    font-size: 12px;
    max-height: 300px;
    overflow-y: auto;
    word-break: break-all;
    white-space: pre-wrap;
}
</style> 